// services/api.service.js
import AuthService from './auth.service.js'
import logger from '../utils/logger';
import { extractDetailMessage } from '../utils/errorDetail';

class ApiService {
  constructor() {
    this.baseURL = process.env.REACT_APP_AUTH_API_URL || process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000'
  }

  /**
   * Realizar request HTTP con manejo automático de autenticación
   * @param {string} endpoint - Endpoint de la API (ej: '/auth/login')
   * @param {Object} options - Opciones del fetch (method, body, headers, etc.)
   * @returns {Promise<Object>} Respuesta de la API
   */
  async request(endpoint, options = {}) {
    const token = AuthService.getAccessToken()
    
    // Configuración base del request
    const config = {
      ...options,
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
        ...options.headers,
      }
    }

    // Agregar token automáticamente si está disponible y es válido
    if (token && !AuthService.isTokenExpired()) {
      config.headers.Authorization = `Bearer ${token}`
    }

    const url = endpoint.startsWith('http') ? endpoint : `${this.baseURL}${endpoint}`
    
    try {
      logger.debug(`API Request: ${config.method || 'GET'} ${url}`);
      
      const response = await fetch(url, config)
      
      // Manejar errores de autenticación automáticamente
      if (response.status === 401) {
        logger.warn('Token expirado o inválido');
        AuthService.logout()
        
        // Redirigir al login si estamos en el browser
        if (typeof window !== 'undefined') {
          window.location.href = '/login'
        }
        
        throw new Error('Session expired - redirecting to login')
      }

      // Intentar parsear JSON
      let data
      const contentType = response.headers.get('content-type')
      
      if (contentType && contentType.includes('application/json')) {
        data = await response.json()
      } else {
        data = { message: await response.text() }
      }
      
      // Manejar errores HTTP
      if (!response.ok) {
        const detailMessage = data.detail && extractDetailMessage(data.detail)
        const errorMessage = data.message || data.error || detailMessage || `HTTP Error ${response.status}`
        throw new Error(errorMessage)
      }

      logger.api(config.method || 'GET', url, response.status);
      return data
      
    } catch (error) {
      logger.error('API Error:', error.message);
      throw error
    }
  }

  /**
   * Realizar request GET
   * @param {string} endpoint - Endpoint de la API
   * @param {Object} options - Opciones adicionales
   * @returns {Promise<Object>} Respuesta de la API
   */
  get(endpoint, options = {}) {
    return this.request(endpoint, { 
      ...options, 
      method: 'GET' 
    })
  }

  /**
   * Realizar request POST
   * @param {string} endpoint - Endpoint de la API
   * @param {Object} body - Datos a enviar
   * @param {Object} options - Opciones adicionales
   * @returns {Promise<Object>} Respuesta de la API
   */
  post(endpoint, body = null, options = {}) {
    return this.request(endpoint, {
      ...options,
      method: 'POST',
      body: body ? JSON.stringify(body) : null
    })
  }

  /**
   * Realizar request PUT
   * @param {string} endpoint - Endpoint de la API
   * @param {Object} body - Datos a enviar
   * @param {Object} options - Opciones adicionales
   * @returns {Promise<Object>} Respuesta de la API
   */
  put(endpoint, body = null, options = {}) {
    return this.request(endpoint, {
      ...options,
      method: 'PUT',
      body: body ? JSON.stringify(body) : null
    })
  }

  /**
   * Realizar request PATCH
   * @param {string} endpoint - Endpoint de la API
   * @param {Object} body - Datos a enviar
   * @param {Object} options - Opciones adicionales
   * @returns {Promise<Object>} Respuesta de la API
   */
  patch(endpoint, body = null, options = {}) {
    return this.request(endpoint, {
      ...options,
      method: 'PATCH',
      body: body ? JSON.stringify(body) : null
    })
  }

  /**
   * Realizar request DELETE
   * @param {string} endpoint - Endpoint de la API
   * @param {Object} options - Opciones adicionales
   * @returns {Promise<Object>} Respuesta de la API
   */
  delete(endpoint, options = {}) {
    return this.request(endpoint, { 
      ...options, 
      method: 'DELETE' 
    })
  }

  // =============================================================================
  // MÉTODOS ESPECÍFICOS PARA AUTH
  // =============================================================================

  /**
   * Realizar request público (sin autenticación)
   * @param {string} endpoint - Endpoint de la API
   * @param {Object} options - Opciones del request
   * @returns {Promise<Object>} Respuesta de la API
   */
  async publicRequest(endpoint, options = {}) {
    const config = {
      ...options,
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
        ...options.headers,
      }
    }

    // NO agregar token en requests públicos
    delete config.headers.Authorization

    const url = endpoint.startsWith('http') ? endpoint : `${this.baseURL}${endpoint}`
    
    try {
      logger.debug(`Public API Request: ${config.method || 'GET'} ${url}`);
      
      const response = await fetch(url, config)
      
      let data
      const contentType = response.headers.get('content-type')
      
      if (contentType && contentType.includes('application/json')) {
        data = await response.json()
      } else {
        data = { message: await response.text() }
      }
      
      if (!response.ok) {
        const detailMessage = data.detail && extractDetailMessage(data.detail)
        const errorMessage = data.message || data.error || detailMessage || `HTTP Error ${response.status}`
        throw new Error(errorMessage)
      }

      logger.api(config.method || 'GET', url, response.status);
      return data
      
    } catch (error) {
      logger.error('Public API Error:', error.message);
      throw error
    }
  }

  /**
   * Realizar request autenticado (requiere token válido)
   * @param {string} endpoint - Endpoint de la API
   * @param {Object} options - Opciones del request
   * @returns {Promise<Object>} Respuesta de la API
   */
  async authenticatedRequest(endpoint, options = {}) {
    const token = AuthService.getAccessToken()
    
    if (!token || AuthService.isTokenExpired()) {
      throw new Error('No valid authentication token available')
    }

    const config = {
      ...options,
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
        'Authorization': `Bearer ${token}`,
        ...options.headers,
      }
    }

    return this.request(endpoint, config)
  }

  // =============================================================================
  // MÉTODOS DE CONVENIENCIA
  // =============================================================================

  /**
   * Upload de archivos
   * @param {string} endpoint - Endpoint de la API
   * @param {FormData} formData - Datos del formulario con archivos
   * @param {Object} options - Opciones adicionales
   * @returns {Promise<Object>} Respuesta de la API
   */
  async upload(endpoint, formData, options = {}) {
    const token = AuthService.getAccessToken()
    
    const config = {
      ...options,
      method: 'POST',
      body: formData,
      headers: {
        // NO agregar Content-Type para FormData (el browser lo hace automáticamente)
        'Accept': 'application/json',
        ...options.headers,
      }
    }

    // Agregar token si está disponible
    if (token && !AuthService.isTokenExpired()) {
      config.headers.Authorization = `Bearer ${token}`
    }

    return this.request(endpoint, config)
  }

  /**
   * Download de archivos
   * @param {string} endpoint - Endpoint de la API
   * @param {string} filename - Nombre del archivo a descargar
   * @param {Object} options - Opciones adicionales
   * @returns {Promise<void>} Inicia la descarga
   */
  async download(endpoint, filename, options = {}) {
    const token = AuthService.getAccessToken()
    
    const config = {
      ...options,
      method: 'GET',
      headers: {
        ...options.headers,
      }
    }

    if (token && !AuthService.isTokenExpired()) {
      config.headers.Authorization = `Bearer ${token}`
    }

    const url = endpoint.startsWith('http') ? endpoint : `${this.baseURL}${endpoint}`
    
    try {
      const response = await fetch(url, config)
      
      if (!response.ok) {
        throw new Error(`Download failed: ${response.status}`)
      }

      const blob = await response.blob()
      
      // Crear link de descarga
      const downloadUrl = window.URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = downloadUrl
      link.download = filename
      link.click()
      
      // Limpiar
      window.URL.revokeObjectURL(downloadUrl)
      
    } catch (error) {
      console.error(`❌ Download Error: ${error.message}`)
      throw error
    }
  }

  // =============================================================================
  // UTILIDADES
  // =============================================================================

  /**
   * Construir URL con query parameters
   * @param {string} endpoint - Endpoint base
   * @param {Object} params - Parámetros de query
   * @returns {string} URL completa con parámetros
   */
  buildUrl(endpoint, params = {}) {
    const url = new URL(endpoint, this.baseURL)
    
    Object.keys(params).forEach(key => {
      if (params[key] !== null && params[key] !== undefined) {
        url.searchParams.append(key, params[key])
      }
    })
    
    return url.toString()
  }

  /**
   * Verificar si la API está disponible
   * @returns {Promise<boolean>} true si la API responde
   */
  async healthCheck() {
    try {
      await this.get('/health')
      return true
    } catch (error) {
      console.error('Health check failed:', error)
      return false
    }
  }
}

// Exportar instancia singleton
export default new ApiService()